import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
} from '@mui/material';
import { saveLesson } from '../utils/lessonUtils';
import { testsAPI } from '../services/api';
import { toast } from 'react-toastify';
import Button from '../components/UIKit/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import LessonEditor from '../components/UIKit/LessonEditor';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProfessions, selectAllProfessions, selectProfessionsLoading } from '../redux/slices/professionsSlice';
import { fetchSkills, selectAllSkills, selectSkillsLoading } from '../redux/slices/skillsSlice';

const CreateLessonPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation(['lessons', 'common']);
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);

  // Redux селекторы для профессий и навыков
  const professions = useSelector(selectAllProfessions);
  const professionsLoading = useSelector(selectProfessionsLoading);
  const skills = useSelector(selectAllSkills);
  const skillsLoading = useSelector(selectSkillsLoading);

  const [lesson, setLesson] = useState({
    title: '',
    content: '',
    duration: 30,
    type: 'text',
    contentType: 'standard',
    isDraft: true,
    image: '',
    resources: [],
    practiceExercises: [],
    professions: [],
    skills: [], // Массив объектов с полной информацией о навыках, responsibilities и tools
    tests: [] // Массив тестов для урока
  });

  // Определяем, является ли пользователь админом или учителем
  const isAdminOrTeacher = user?.userMeta?.roles?.includes('admin') || user?.userMeta?.roles?.includes('teacher');

  // Загружаем профессии и навыки при монтировании компонента
  useEffect(() => {
    if (professions.length === 0 && !professionsLoading) {
      dispatch(fetchProfessions({ all: true, isCommon: 1 }));
    }
    if (skills.length === 0 && !skillsLoading) {
      dispatch(fetchSkills({ all: true, isCommon: 1 }));
    }
  }, [dispatch, professions.length, professionsLoading, skills.length, skillsLoading]);

  const handleSaveLesson = async () => {
    try {
      setSaving(true);

      // Проверка на заполнение обязательных полей
      if (!lesson.title) {
        toast.error(t('create.validation.titleRequired'));
        setSaving(false);
        return;
      }

      // Сначала сохраняем урок
      const response = await saveLesson(lesson);
      const lessonId = response.data.data._id;

      // Если есть тесты, сохраняем их по одному
      if (lesson.tests && lesson.tests.length > 0) {
        try {
          for (const testData of lesson.tests) {
            if (testData._id && 
                !testData._id.startsWith('temp-')) {
              // Обновляем существующий тест с lessonId
              await testsAPI.update(testData._id, { ...testData, lessonId });
              console.log('Тест обновлен:', testData._id);
            } else {
              const { _id, ...testDataWithoutId } = testData;
              await testsAPI.create({ ...testDataWithoutId, lessonId });
              console.log('Тест создан для урока:', lessonId);
            }
          }
          console.log('Все тесты успешно обработаны для урока:', lessonId);
        } catch (testError) {
          console.error('Ошибка при сохранении тестов:', testError);
          toast.warning('Урок сохранен, но произошла ошибка при сохранении тестов');
        }
      }

      toast.success(t('create.saveSuccess'));

      // Переходим на страницу просмотра созданного урока
      navigate(`/lessons/${response.data.data.slug}`);
    } catch (err) {
      console.error('Ошибка при сохранении урока:', err);
      toast.error(
        err.response?.data?.message || t('common:errors.savingFailed')
      );
    } finally {
      setSaving(false);
    }
  };

  const handleBackToLibrary = () => {
    navigate('/lessons');
  };

  return (
    <Container sx={{ mt: 4, mb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Typography variant="h4" component="h1">
          {t('create.title')}
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToLibrary}
            sx={{ mr: 2 }}
          >
            {t('create.buttons.back')}
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveLesson}
            disabled={saving}
          >
            {saving
              ? t('create.buttons.saving')
              : t('create.buttons.save')}
          </Button>
        </Box>
      </Box>

      <LessonEditor
        lesson={lesson}
        onChange={setLesson}
        isAdminOrTeacher={isAdminOrTeacher}
        header={
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h5" component="h2">
              {lesson.title || t('create.newLesson')}
            </Typography>
          </Box>
        }
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 4,
        }}
      >
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSaveLesson}
          disabled={saving}
          size="large"
        >
          {saving
            ? t('create.buttons.saving')
            : t('create.buttons.save')}
        </Button>
      </Box>
    </Container>
  );
};

export default CreateLessonPage; 